// ── The workspace brand, browser side (multi-tenant phase 7) ─────────────────────────────────────
// /api/me carries `brand` (server: firmSettings.brandName — Admin → Companies → Workspace → Brand name,
// else the accounting firm's short name). Every logo alt, home-button title and window title reads it
// here; nothing in the client spells a firm's name. '' until /api/me has answered.
//
// It is also the {{brand}} every translation may print without its caller passing it: src/i18n/index.ts
// starts i18next with interpolation.defaultVariables = { brand: brand() }, and every change made here is
// written into that same object, then mounted screens are told to translate again. So an i18n value says
// '{{brand}}™ Tax' or 'Bills from {{brand}}' and no t() call site has to know.
//
// Pages that never get /api/me (the client portal, public share pages) take the brand from the public
// endpoint GET /api/public/brand instead — loadPublicBrand() below; a signed-in /api/me answer wins.
//
// 'i18next' is the package's own singleton (the instance src/i18n/index.ts initialises), so importing it
// here creates no cycle with src/i18n/index.ts, which imports this file.
import i18next from 'i18next';

let current = '';
let labels: { legal: string; accounting: string } = { legal: '', accounting: '' };

/** Write the brand into i18next's default interpolation variables and make mounted screens re-translate.
 *  Before i18n is initialised there is nothing to write: the init reads brand() itself. */
const syncTranslations = (): void => {
  const dv = (i18next.options?.interpolation as any)?.defaultVariables;
  if (!dv || typeof dv !== 'object' || dv.brand === current) return;
  dv.brand = current;   // mutated in place: i18next keeps references to this very object
  // react-i18next re-renders every useTranslation() on 'languageChanged' (its default bindI18n); no other
  // code in the app listens to that event. Deferred, so a call made while React renders never updates
  // other components in the middle of that render.
  Promise.resolve().then(() => {
    try { if (i18next.isInitialized) i18next.emit('languageChanged', i18next.language); } catch { /* nothing mounted */ }
  });
};

export const setBrand = (b: any, firmLabels?: { legal?: string; accounting?: string } | null): void => {
  current = String(b || '').trim();
  labels = { legal: String(firmLabels?.legal || '').trim(), accounting: String(firmLabels?.accounting || '').trim() };
  syncTranslations();
};

let publicPending: Promise<string> | null = null;
/** For pages with no /api/me (the client portal's bootstrap, public pages): a brand handed in — e.g. one a
 *  profile response carries — is used as is; otherwise GET /api/public/brand is asked ONCE per page load,
 *  and its answer is applied only while nothing has set the brand (a signed-in /api/me answer always wins).
 *  Resolves to the brand in force ('' when none is set or the server cannot be reached). */
export function loadPublicBrand(given?: any): Promise<string> {
  const g = String(given || '').trim();
  if (g) { current = g; syncTranslations(); return Promise.resolve(current); }
  if (current) return Promise.resolve(current);
  if (!publicPending) {
    // Imported lazily: the API module pulls in the request layer, which a translation file must not load
    // at import time.
    publicPending = import('./api/ctrlplane')
      .then(m => m.ctrlplaneApi.publicBrand())
      .then(r => String(r?.brand || '').trim())
      .catch(() => { publicPending = null; return ''; });   // unreachable: the next caller asks again
  }
  return publicPending.then(b => {
    if (b && !current) { current = b; syncTranslations(); }
    return current;
  });
}

/** The short label of the firm of this kind ('' when none is marked) — what source_label tags carry. */
export const firmLabel = (kind: 'legal' | 'accounting'): string => labels[kind];
/** firmLabel(kind) or a neutral word, for tabs and chips before /api/me answers. */
export const firmLabelOr = (kind: 'legal' | 'accounting', fallback: string): string => labels[kind] || fallback;
export const brand = (): string => current;
/** The brand, or `fallback` while it is unknown / unset. */
export const brandOr = (fallback: string): string => current || fallback;
/** "<brand> Accounting" → "Accounting": the short label chips use. */
export const stripBrand = (name: any): string => {
  const n = String(name || '');
  return current && n.startsWith(current + ' ') ? n.slice(current.length + 1) : n;
};
